import { FC } from 'react'

import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { DOCUMENT, GENDER } from 'entities/user/module'

import { DetailsAppeal } from 'shared/api/BuyTicketModel'
import { MyTicketItem } from 'shared/api/MyTicketsModel.ts'
import { ADDITIONAL_SERVICE, TYPE_AGE, TYPE_APPELS } from 'shared/constants'
import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import { priceShow } from 'shared/lib//priceShow'
import { showWrongDateFormat } from 'shared/lib/dateFormat.ts'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { Paper } from 'shared/ui/paper'

import styles from './styles.module.scss'

interface ItemProps {
  title?: string
  label?: string
  width?: number
}

interface Props {
  ticket?: MyTicketItem
  additionalService?: DetailsAppeal[]
}

const RenderItem = ({ title, label, width = 100 }: ItemProps) => {
  return (
    <div
      className={styles.itemWrapper}
      style={{ width: `${width}%` }}
    >
      <span className={styles.label}>{label}</span>
      <p className={styles.value}>{title}</p>
    </div>
  )
}

const DetailPassengers: FC<Props> = ({ ticket, additionalService }) => {
  const { t } = useTranslation()

  const params = useParams()

  const { passenger } = params

  const passengerData = ticket?.flight_details?.tickets?.[0]?.passengers
    ?.map((item, index) => {
      if (item.id === parseInt(passenger || '')) {
        return { selectedPassenger: item, index }
      }
    })
    .filter((item) => item)[0]

  if (!passengerData?.selectedPassenger) {
    return null
  }

  const { selectedPassenger, index } = passengerData

  const typesDocuments = [
    { name: DOCUMENT.ID, option: t('id_passport') },
    { name: DOCUMENT.PASSPORT, option: t('passport') },
    { name: DOCUMENT.BIRTH_CERTIFICATE, option: t('birth_certificate') },
  ]

  const optionsGender = [
    { name: GENDER.MALE, option: t('male') },
    { name: GENDER.FEMALE, option: t('female') },
  ]

  const listAges = [
    { name: TYPE_AGE.ADULTS, option: t('purchase_page.adults_count') },
    { name: TYPE_AGE.CHILDREN, option: t('purchase_page.children_count') },
    // {
    //   name: TYPE_AGE.INFANTS_WITH_SEAT,
    //   option: t('purchase_page.infants_count'),
    // },
    {
      name: TYPE_AGE.INFANTS_WITHOUT_SEAT,
      option: t('purchase_page.infants_count'),
    },
  ]

  const passengerDocument = typesDocuments?.filter(
    (item) => item.name === selectedPassenger?.document?.type,
  )[0]

  const userGender = optionsGender?.filter(
    (item) => item.name === selectedPassenger?.gender,
  )[0]

  const listDetail = [
    {
      label: t('type_passport'),
      title: passengerDocument?.option || '',
    },
    {
      label: t('last_name'),
      title: `${selectedPassenger?.name?.last}`,
    },
    {
      label: t('first_name'),
      title: `${selectedPassenger?.name?.first}`,
    },
    // {
    //   label: t('middle_name'),
    //   title: `${selectedPassenger?.name?.middle || ''}`,
    // },
    {
      label: t('gender'),
      title: userGender?.option,
    },
    {
      label: t('birthdate'),
      title:
        showWrongDateFormat(
          selectedPassenger?.birthdate,
          DATE_FORMAT_LIST.FULL_DATE,
        ) || '',
    },
    {
      label: t('citizenship'),
      title: selectedPassenger?.citizenship,
    },
    {
      label: t('serial_documents'),
      title: selectedPassenger?.document?.original_number,
    },
  ]

  const typeAge = listAges?.filter(
    (item) => item.name === selectedPassenger?.age,
  )[0]

  const listAdditionalService = additionalService?.filter(
    (service) => service.topic === TYPE_APPELS.ADDITIONAL_SERVICE,
  )

  const passengerService = listAdditionalService?.filter((service) => {
    const passenger = service.flight_appeal.appeals_detail.passengers.map(
      (item) => item.passenger_key,
    )

    return passenger.includes(selectedPassenger.key)
  })

  return (
    <div className={styles.container}>
      <HeaderGoBack
        title={t('passenger_data')}
        buttonWhite
      />

      <div className={styles.detail}>
        <p className={styles.title}>{`${typeAge?.option} №${index + 1}`}</p>

        <div className={styles.wrapperDetail}>
          {listDetail?.map((item, index) => (
            <RenderItem
              {...item}
              key={index}
            />
          ))}
        </div>
      </div>

      {passengerService?.map((service, index) => {
        const servicePassenger =
          service.flight_appeal.appeals_detail.passengers.filter(
            (passenger) => passenger.passenger_key === selectedPassenger.key,
          )[0]

        return (
          <div
            className={styles.serviceItem}
            key={service.id}
          >
            <Paper padding={[16]}>
              <div className={styles.information}>
                <h3>{`${t('additional_service_title')} №${index + 1}`}</h3>

                <div className={styles.status}>
                  <p>{t('status')}</p>

                  {servicePassenger.status === ADDITIONAL_SERVICE.APPROVED ? (
                    <span className={styles.approved}>{t('approved')}</span>
                  ) : null}

                  {servicePassenger.status === ADDITIONAL_SERVICE.CLOSED ? (
                    <span className={styles.closed}>{t('closed')}</span>
                  ) : null}

                  {ADDITIONAL_SERVICE.WORKING === servicePassenger.status ? (
                    <span className={styles.wrapperStatus}>
                      {t('status_names.pending')}
                    </span>
                  ) : null}
                </div>

                <div className={styles.informationBlock}>
                  <span>{t('title_service')}</span>

                  <p>{service?.flight_appeal?.topic}</p>
                </div>

                {servicePassenger?.price_of_service ? (
                  <div className={styles.informationBlock}>
                    <span>{t('price')}</span>

                    <p>
                      {priceShow(servicePassenger?.price_of_service)} &#8384;
                    </p>
                  </div>
                ) : null}
              </div>
            </Paper>
          </div>
        )
      })}
    </div>
  )
}

export default DetailPassengers
